/**
 * `orgs` namespace — user-facing strings for the Organizations & access admin
 * area (`src/orgs/`). FLAT camelCase keys, one per line (ADR 0065). Plural keys
 * use i18next `_one`/`_other` suffixes (Intl.PluralRules) with `{{count}}`.
 * Role/scope CODES stay as wire data; only their display LABELS live here.
 */
export const messages = {
  // OrgsPage — header
  pageEyebrow: 'Settings',
  pageTitle: 'Organizations & access',
  pageLede:
    'Organize your team and control who can do what. Create teams, add members, and assign roles — a member’s access comes only from its assigned roles, not its place in the org chart.',

  // OrgsListPanel
  orgsHeading: 'Organizations',
  workspaceHeading: 'Workspace',
  workspaceVsOrgExplainer: 'A workspace is a separate space with its own members and data. Organizations are groups inside the workspace you are currently in.',
  newWorkspacePlaceholder: 'New workspace name',
  newWorkspaceAriaLabel: 'New workspace name',
  createWorkspace: 'Create workspace',
  workspaceCreateFailed: 'The workspace could not be created.',
  newOrgPlaceholder: 'New organization name',
  newOrgAriaLabel: 'New organization name',
  createOrgRequiresScope: 'Only workspace owners can create organizations',
  noOrgsTitle: 'No organizations yet',
  noOrgsBody: 'Create one to add teams and members.',
  deleteOrgAriaLabel: 'Delete {{name}}',

  // OrgDetailPanel — empty state
  selectOrgTitle: 'Select an organization',
  selectOrgBody: 'Pick an organization on the left to manage its teams and members.',

  // OrgDetailPanel — "View as" lens
  viewAsLabel: 'View as',
  viewAsOwnerOption: 'You — your own access',
  viewAsRoleGroupLabel: 'Preview a role (UI only)',
  viewAsRoleOption: 'As {{name}}',
  viewAsMemberGroupLabel: 'View as member (enforced)',
  viewAsMemberNoRoles: 'no roles',
  viewAsMemberOption: '{{name}} ({{roles}})',
  previewingRolePrefix: 'Previewing the',
  previewingRoleSuffix_one: 'role — {{count}} scope. This is a UI preview (nothing is granted); actions this role can’t perform are disabled below.',
  previewingRoleSuffix_other: 'role — {{count}} scopes. This is a UI preview (nothing is granted); actions this role can’t perform are disabled below.',
  enforcingMemberPrefix: 'Enforcing as',
  enforcingMemberSuffix_one: '— {{count}} scope. Actions this member can’t perform are disabled below; attempting one server-side returns 403.',
  enforcingMemberSuffix_other: '— {{count}} scopes. Actions this member can’t perform are disabled below; attempting one server-side returns 403.',

  // TeamsPanel
  teamsHeading: 'Teams',
  newTeamPlaceholder: 'New team name',
  newTeamAriaLabel: 'New team name',
  addTeam: 'Add team',
  addTeamRequiresScope: 'Requires host:teams:manage',
  noTeamsYet: 'No teams yet.',
  deleteTeamAriaLabel: 'Delete team {{name}}',

  // MembersPanel
  membersHeading: 'Members',
  memberNamePlaceholder: 'Name',
  memberNameAriaLabel: 'Member name',
  memberEmailPlaceholder: 'Email (optional)',
  memberEmailAriaLabel: 'Member email',
  addMember: 'Add member',
  addMemberRequiresScope: 'Requires host:members:manage',
  noMembersTitle: 'No members yet',
  noMembersBody: 'Add a member above and assign roles.',
  accessButton: 'Access',
  rolesButton: 'Roles',
  editRolesAriaLabel: 'Edit roles for {{name}}',
  removeMemberAriaLabel: 'Remove {{name}}',
  noRoles: 'no roles',
  effectiveScopesBasis: 'Effective scopes (basis: {{basis}}) — resolved from assigned roles only:',
  noScopesFailClosed: 'no scopes (fail-closed)',

  // GroupsPanel
  groupsHeading: 'Groups',
  groupsHeadingSuffix: '· role bundles',
  groupsIntro:
    'A group bundles roles and grants them to its members — on top of each member’s own roles. Use it for batch access management (e.g. “Editors”, “Admins”).',
  newGroupPlaceholder: 'New group name',
  newGroupAriaLabel: 'New group name',
  addGroup: 'Add group',
  addGroupRequiresScope: 'Requires host:groups:manage',
  noGroupsYet: 'No groups yet.',
  membersButton: 'Members',
  editGroupMembersAriaLabel: 'Edit members of {{name}}',
  deleteGroupAriaLabel: 'Delete group {{name}}',
  noGroupRoles: 'no roles',
  groupMemberCount_one: '{{count}} member',
  groupMemberCount_other: '{{count}} members',
  groupMemberListSuffix: ': {{names}}',
  addMembersToOrgFirst: 'Add members to the org first.',

  // RoleCatalogPanel
  roleCatalogHeading: 'Role catalog',
  roleCatalogIntro:
    'Built-in roles and the scopes they grant. Bare scopes are OpenWOP protocol scopes; host: scopes manage this org/team/member surface.',

  // CustomRolesPanel
  customRolesHeading: 'Custom roles',
  customRolesHeadingSuffix: '· define your own',
  customRolesIntro:
    'Bundle any scopes into a named role, then assign it to members and groups exactly like a built-in role.',
  newRolePlaceholder: 'New role name',
  newRoleAriaLabel: 'New custom role name',
  createRole: 'Create role',
  createRoleRequiresScope: 'Requires host:roles:manage',
  noCustomRolesYet: 'No custom roles yet.',
  deleteRoleAriaLabel: 'Delete role {{name}}',
  noScopes: 'no scopes',

  // Built-in role display labels (codes are persisted wire data; these are UI copy)
  roleLabelViewer: 'viewer',
  roleLabelEditor: 'editor',
  roleLabelAdmin: 'admin',
  roleLabelOwner: 'owner',

  // useOrgsController — confirm dialogs
  confirmDeleteOrg: 'Delete organization "{{name}}" and all its teams + members? This can’t be undone.',
  confirmDeleteTeam: 'Delete team "{{name}}"?',
  confirmRemoveMember: 'Remove member "{{name}}"?',
  confirmDeleteGroup: 'Delete group "{{name}}"? Members keep their direct roles.',
  confirmDeleteRole: 'Delete custom role "{{name}}"? It will be removed from any member or group that has it.',

  // useOrgsController — error toast format ("{{title}} — {{detail}}")
  errorWithDetail: '{{title}} — {{detail}}',
  // Email invitations (ADR 0004 UI; AUTH-3)
  invitesHeading: 'Invite by email',
  inviteEmailPlaceholder: 'colleague@company.com',
  inviteEmailAriaLabel: 'Invitee email address',
  inviteRoleAriaLabel: 'Role for the invitee',
  inviteSend: 'Send invite',
  inviteCreated: 'Invitation created.',
  inviteEmailed: 'Invitation emailed to {{email}}.',
  inviteLinkReady: 'Invitation created — share the accept link with the invitee.',
  inviteCreateFailed: 'The invitation couldn’t be created. Try again.',
  inviteInvalidEmail: 'That doesn’t look like a valid email address — check it and send again. Nothing was created.',
  inviteRevokeFailed: 'Revoking the invitation failed. Try again.',
  inviteUndeliverable: 'This invitation couldn’t be delivered — this organization has no email sender or your email connection is unavailable. Set one up on the Email page and try again.',
  inviteDeliveryFailed: 'Email delivery failed — nothing was kept. Try again in a moment.',
  acceptSignInFallback: 'Go to sign-in',
  invitesListFailed: 'Pending invitations couldn’t be loaded — there may be some you’re not seeing.',
  invitesLoading: 'Loading pending invitations…',
  invitesEmpty: 'No pending invitations.',
  inviteExpiredChip: 'Expired',
  inviteResend: 'Resend',
  inviteResendTitle: 'Resend invitation',
  inviteResendBody: 'Send a fresh invitation to {{email}}? This creates a new link — any link previously sent to this address stops working immediately.',
  inviteCopyLink: 'Copy invite link',
  inviteLinkCopied: 'Invite link copied.',
  inviteExpires: 'expires {{when}}',
  inviteInvitedBy: 'invited by {{name}}',
  inviteRevoke: 'Revoke',
  inviteRevokeTitle: 'Revoke invitation',
  inviteRevokeBody: 'Revoke the pending invitation for {{email}}? The link stops working immediately.',
  inviteRevoked: 'Invitation for {{email}} revoked.',
  acceptNoTokenTitle: 'Missing invitation token',
  acceptNoTokenBody: 'This page redeems an organization invitation, but the link is missing its token. Ask the sender for a fresh invite link.',
  acceptLoadingTitle: 'Loading your invitation…',
  acceptInvitedBy: '{{name}} invited you',
  acceptExpiredTitle: 'This invitation has expired',
  acceptExpiredBody: 'Invitations are valid for 7 days. Ask the person who invited you to send a new one.',
  acceptGoneTitle: 'This invitation is no longer valid',
  acceptGoneBody: 'It may have been used already or revoked. Ask the person who invited you to send a new one.',
  acceptLoadFailedTitle: 'Couldn’t load this invitation',
  acceptLoadFailedBody: 'Something went wrong on our end — the invitation most likely still exists. Try again.',
  acceptAlreadyMemberTitle: 'You’re already a member',
  acceptAlreadyMemberBody: 'This account already belongs to the organization — nothing was changed.',
  acceptAlreadyMemberBodyNamed: 'This account already belongs to {{org}} — nothing was changed.',
  acceptSignInTitle: 'Sign in to accept your invitation',
  acceptSignInBody: 'Invitations are bound to your email address — sign in with the invited account to join the organization.',
  acceptDoneTitle: 'You have joined the organization',
  acceptDoneBody: 'Your membership is active with the invited role.',
  acceptHeadTitle: 'Accept your invitation',
  acceptPreviewTitle: 'Join {{org}}',
  acceptPreviewRole: 'You’ll join as {{role}}.',
  acceptPreviewEmail: 'Invitation sent to {{email}}',
  acceptPreviewExpires: 'Expires',
  acceptCta: 'Accept invitation',
  acceptDoneBodyNamed: 'You’re now a member of {{org}}.',
  acceptErrorMismatch: 'This invitation was sent to a different email address. Sign in with that address to accept it.',
  acceptSwitchAccount: 'Switch account',
  acceptGoToOrg: 'Open Organizations',
  acceptGoToOrgNamed: 'Open {{org}}',
  acceptCrossWorkspaceNote: "This organization lives in another workspace — opening it will switch your active workspace.",
  acceptCrossWorkspaceNoteNamed: "This organization lives in the {{workspace}} workspace — opening it will switch your active workspace.",
  acceptCrossWorkspaceNoAccess: "You have joined, but this organization lives in a workspace your account cannot open yet — ask the person who invited you for workspace access.",
  acceptErrorBody: 'The invitation may be expired, revoked, or issued to a different email address.',
  // ADR 0564 D3 — recipient-side Decline (confirm-gated) + the terminal state
  acceptDeclineCta: 'Decline',
  acceptDeclineTitle: 'Decline the invitation to {{org}}?',
  acceptDeclineBody: '{{org}} will see that you declined. If you change your mind later, you’ll need to ask them to invite you again.',
  acceptDeclineConfirm: 'Decline invitation',
  acceptDeclinedTitle: 'You declined this invitation',
  acceptDeclinedBodyNamed: 'You’re not a member of {{org}}, and the people who invited you can see that you declined. Changed your mind? Ask them to re-invite you.',
  acceptDeclinedBody: 'This link no longer works, and the person who invited you can see that you declined. Changed your mind? Ask them to re-invite you.',
  acceptDeclineFailed: 'Declining didn’t go through. Try again.',
  acceptSignInDeclineNote: 'Declining also needs you to sign in — the invitation is bound to the same address either way.',
  // ADR 0622 D7 — the self-set (unverified) address gate on accept AND decline
  acceptErrorUnverified: 'The email address on your account was set by you and isn’t verified, so it can’t be used to act on this invitation. Sign in through your identity provider so your verified address is used, or contact the person who invited you.',
  // ADR 0564 D4 — the inviter's view of a decline
  inviteDeclinedChip: 'Declined',
  inviteDeclinedAt: 'Declined {{when}}',
  inviteDeclinedWhen: 'declined {{when}}',
  inviteResendBodyDeclined: 'Send a fresh invitation to {{email}}? They declined the previous one. This creates a new link — any link previously sent to this address stops working immediately.',
  // ADR 0622 D5 — the re-invite failed but the EARLIER invitation is still valid
  inviteDeliveryFailedPriorValid: 'Email delivery failed — an earlier invitation for this address may still be valid; nothing about it was changed. Try again in a moment.',
  inviteUndeliverablePriorValid: 'This invitation couldn’t be delivered — this organization has no email sender or your email connection is unavailable. An earlier invitation for this address may still be valid; nothing about it was changed. Set a sender up on the Email page and try again.',
  // ADR 0815 — owner-tier locks, typed refusals, and what removing a member does
  confirmRemoveMemberBody: 'They leave the workspace now. Their API keys stop working, invitations they sent that are still pending are cancelled, and schedules that run as them are paused. You can resume most of those schedules, which makes you their owner.',
  removeMemberConfirm: 'Remove member',
  memberRemoved: 'Removed {{name}}',
  ownerRowLocked: 'Only an owner can change or remove an owner',
  ownerRowsLockedNote: 'Owners can only be changed or removed by another owner.',
  ownerGroupLocked: 'This group grants owner access, so only an owner can change it',
  errorOwnerTier: 'Only an owner can do that.',
  errorLastOwner: 'This is the workspace’s last owner. Make someone else an owner first, then try again.',
  errorIdentityLocked: 'A workspace member’s sign-in identity can’t be changed in place. Remove them and send a new invitation instead.',
  errorIdentityTaken: 'That identity is already a member of this organization.',
  errorTransferToSelf: 'Choose another member to hand ownership to.',
  confirmRemoveSubOrgMemberBody: 'They lose access to this organization, and invitations to it that they sent and are still pending are cancelled. Their workspace membership is not affected.',
  confirmRemoveMemberBodyNeutral: 'They lose access to this organization, and invitations to it that they sent and are still pending are cancelled. If this was their last membership here, their API keys also stop working and schedules that run as them are paused.',
  ownerGroupsLockedNote: 'Groups that grant owner access can only be changed by an owner.',
  errorCreateOrgOwnerRequired: 'Only an owner can create an organization here. Ask an owner of an existing organization to make you an owner, then try again.',
  errorForbiddenScope: 'You don’t have permission to do that in this organization. Ask a workspace owner or admin.',
} as const;
